import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { getSeoRoute, seoSiteConfig } from "@/seo/config";

const MANAGED_ATTRIBUTE = "data-seo-managed";
const SCHEMA_ATTRIBUTE = "data-seo-schema";

const upsertMeta = (selector: string, attributes: Record<string, string>, content: string) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => element!.setAttribute(key, value));
  element.setAttribute("content", content);
  element.setAttribute(MANAGED_ATTRIBUTE, "true");
};

const upsertLink = (selector: string, attributes: Record<string, string>) => {
  let element = document.head.querySelector<HTMLLinkElement>(selector);

  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => element!.setAttribute(key, value));
  element.setAttribute(MANAGED_ATTRIBUTE, "true");
};

const replaceStructuredData = (schemas: Record<string, unknown>[]) => {
  document.head.querySelectorAll(`script[type="application/ld+json"][${SCHEMA_ATTRIBUTE}]`).forEach((node) => {
    node.remove();
  });

  schemas.forEach((schema) => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute(SCHEMA_ATTRIBUTE, "true");
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
  });
};

const Seo = () => {
  const location = useLocation();
  const seo = getSeoRoute(location.pathname);

  useEffect(() => {
    document.documentElement.lang = "de";
    document.title = seo.title;

    upsertMeta('meta[name="description"]', { name: "description" }, seo.description);
    upsertMeta('meta[name="robots"]', { name: "robots" }, seo.robotsContent);
    upsertMeta('meta[name="googlebot"]', { name: "googlebot" }, seo.robotsContent);
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name" }, seoSiteConfig.siteName);
    upsertMeta('meta[property="og:locale"]', { property: "og:locale" }, seoSiteConfig.locale);
    upsertMeta('meta[property="og:type"]', { property: "og:type" }, seo.ogType);
    upsertMeta('meta[property="og:url"]', { property: "og:url" }, seo.canonicalUrl);
    upsertMeta('meta[property="og:title"]', { property: "og:title" }, seo.title);
    upsertMeta('meta[property="og:description"]', { property: "og:description" }, seo.description);
    upsertMeta('meta[property="og:image"]', { property: "og:image" }, seo.imageUrl);
    upsertMeta('meta[property="og:image:alt"]', { property: "og:image:alt" }, seo.imageAlt);
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card" }, "summary_large_image");
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title" }, seo.title);
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description" }, seo.description);
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image" }, seo.imageUrl);
    upsertMeta('meta[name="twitter:image:alt"]', { name: "twitter:image:alt" }, seo.imageAlt);
    upsertLink('link[rel="canonical"]', { rel: "canonical", href: seo.canonicalUrl });
    replaceStructuredData(seo.structuredData);
  }, [seo]);

  return null;
};

export default Seo;
